import React, { useState, type JSX } from "react";

import type {
  AdminRecognitionComparisonMatrix,
  AdminRecognitionRawJsonComparison,
} from "../../contracts/admin-read-models";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { RecognitionModelComparisonMatrix } from "./recognition-model-comparison-matrix";
import type { RecognitionModelComparisonCardViewModel } from "./recognition-model-comparison-view-model";
import type { RecognitionRawArtifact } from "./recognition-raw-artifact-viewer";

type RecognitionModelComparisonPanelProps = {
  cards: readonly RecognitionModelComparisonCardViewModel[];
  groupStatusLabel: string;
  locale: AdminDashboardLocale;
  resultMatrix: AdminRecognitionComparisonMatrix;
  rawJsonComparison?: AdminRecognitionRawJsonComparison;
  rawVisibility: "visible" | "policy_hidden" | "not_available";
  onRequestRawAccess: (artifact: RecognitionRawArtifact) => void;
};

function toRawArtifact(card: RecognitionModelComparisonCardViewModel): RecognitionRawArtifact {
  return {
    action: card.rawAction,
    id: card.variantKey,
    kind: "json",
    label: `${card.rank}. ${card.model}`,
  };
}

export function RecognitionModelComparisonPanel(props: RecognitionModelComparisonPanelProps): JSX.Element {
  const firstRawCard = props.cards.find((card) => card.rawResponseJson !== undefined);
  const [selectedRawVariantKey, setSelectedRawVariantKey] = useState(firstRawCard?.variantKey ?? "");
  const selectedRawCard = props.cards.find((card) => card.variantKey === selectedRawVariantKey && card.rawResponseJson !== undefined) ?? firstRawCard;
  const rawCount = props.cards.filter((card) => card.rawResponseJson !== undefined).length;
  const copy = createCopy(props.locale);

  return (
    <section aria-label={copy.title} style={{ display: "grid", gap: "18px", minWidth: 0 }}>
      <div style={{ display: "grid", gap: "4px" }}>
        <h2 style={{ margin: 0 }}>{copy.title} · {props.groupStatusLabel}</h2>
        <span style={{ color: "#64748b", fontSize: "0.9rem" }}>{rawCount} {copy.rawStored}</span>
      </div>

      <RecognitionModelComparisonMatrix
        cards={props.cards}
        locale={props.locale}
        matrix={props.resultMatrix}
        title={copy.resultComparison}
      />

      <section aria-label={copy.rawComparison} style={{ display: "grid", gap: "12px", padding: "14px", border: "1px solid rgba(15,118,110,.22)", borderRadius: "16px", background: "rgba(240,253,250,.48)", minWidth: 0 }}>
        <h3 style={{ margin: 0 }}>{copy.rawComparison}</h3>
        {props.rawVisibility === "policy_hidden" ? <p style={noticeStyle}>{copy.policyHidden}</p> : null}
        {props.rawVisibility === "not_available" ? <p style={noticeStyle}>{copy.rawUnavailable}</p> : null}
        {props.rawVisibility === "visible" && props.rawJsonComparison ? (
          <RecognitionModelComparisonMatrix
            cards={props.cards}
            locale={props.locale}
            matrix={props.rawJsonComparison}
            title={copy.rawPathComparison}
          />
        ) : null}
        {props.rawVisibility === "visible" ? (
          <div style={{ display: "grid", gap: "10px", minWidth: 0 }}>
            <strong>{copy.rawByModel}</strong>
            <div aria-label={copy.rawModels} role="tablist" style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {props.cards.map((card) => (
                <button
                  aria-selected={selectedRawCard?.variantKey === card.variantKey}
                  disabled={!card.rawResponseJson}
                  key={card.variantKey}
                  onClick={() => setSelectedRawVariantKey(card.variantKey)}
                  role="tab"
                  style={tabStyle(selectedRawCard?.variantKey === card.variantKey)}
                  type="button"
                >
                  {card.rank}. {card.model}
                </button>
              ))}
            </div>
            {selectedRawCard?.rawResponseJson ? (
              <pre role="tabpanel" style={{ margin: 0, maxHeight: "420px", overflow: "auto", padding: "12px", borderRadius: "12px", background: "#0f172a", color: "#e2e8f0", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {JSON.stringify(selectedRawCard.rawResponseJson, null, 2)}
              </pre>
            ) : null}
          </div>
        ) : null}
      </section>

      <section style={{ display: "grid", gap: "10px" }}>
        <h3 style={{ margin: 0, fontSize: "1rem" }}>{copy.modelResults}</h3>
        <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
          {props.cards.map((card) => (
            <article key={card.variantKey} style={{ display: "grid", gap: "8px", padding: "14px", border: "1px solid rgba(24,33,43,.12)", borderRadius: "14px", background: "#fff", minWidth: 0 }}>
              <strong style={{ overflowWrap: "anywhere" }}>{card.rank}. {card.model}</strong>
              {card.isReturned ? <span style={{ color: "#047857", fontWeight: 700 }}>{copy.returned}</span> : null}
              <span>{card.statusLabel} · {card.validationStatusLabel}</span>
              {card.notExecutedReasonLabel ? <span>{card.notExecutedReasonLabel}</span> : null}
              {card.errorCode ? <span>{card.errorCode}</span> : null}
              {card.errorMessageRedacted ? <span>{card.errorMessageRedacted}</span> : null}
              {card.rawResponseStatus === "unavailable" ? <span>{copy.providerRawUnavailable}</span> : null}
              {card.rawResponseStatus === "legacy_missing" ? <span>{copy.legacyRawMissing}</span> : null}
              {!card.rawResponseJson && card.rawAction.state === "available" ? <button onClick={() => props.onRequestRawAccess(toRawArtifact(card))} type="button">{copy.openLegacyRaw}</button> : null}
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function createCopy(locale: AdminDashboardLocale) {
  return {
    title: pickAdminDashboardLocaleText(locale, { en: "Model comparison", ko: "모델 비교" }),
    resultComparison: pickAdminDashboardLocaleText(locale, { en: "Results by model", ko: "모델별 결과 비교" }),
    rawComparison: pickAdminDashboardLocaleText(locale, { en: "Raw JSON comparison", ko: "원문 JSON 비교" }),
    rawPathComparison: pickAdminDashboardLocaleText(locale, { en: "Raw JSON path comparison", ko: "원문 JSON 경로 비교" }),
    rawByModel: pickAdminDashboardLocaleText(locale, { en: "Raw JSON by model", ko: "모델별 원문 JSON" }),
    rawModels: pickAdminDashboardLocaleText(locale, { en: "Raw JSON models", ko: "원문 JSON 모델" }),
    rawStored: pickAdminDashboardLocaleText(locale, { en: "stored raw model responses", ko: "개 모델 원문 저장됨" }),
    modelResults: pickAdminDashboardLocaleText(locale, { en: "Model execution status", ko: "모델별 실행 상태" }),
    returned: pickAdminDashboardLocaleText(locale, { en: "Returned model", ko: "반환 모델" }),
    policyHidden: pickAdminDashboardLocaleText(locale, { en: "Raw output is hidden by retention or security policy.", ko: "보관 또는 보안 정책상 원문 비공개" }),
    rawUnavailable: pickAdminDashboardLocaleText(locale, { en: "Check each model for not executed, no response, or legacy missing states.", ko: "모델별 미실행·원문 미수신·과거 누락 상태를 확인하세요." }),
    providerRawUnavailable: pickAdminDashboardLocaleText(locale, { en: "Provider response was not received", ko: "공급자 원문 미수신" }),
    legacyRawMissing: pickAdminDashboardLocaleText(locale, { en: "Historical raw response was not stored", ko: "과거 원문 누락" }),
    openLegacyRaw: pickAdminDashboardLocaleText(locale, { en: "Open legacy raw JSON", ko: "기존 원문 JSON 열기" }),
  };
}

function tabStyle(selected: boolean) {
  return {
    minHeight: "38px",
    padding: "0 10px",
    borderRadius: "10px",
    border: selected ? "1px solid #0f766e" : "1px solid rgba(148,163,184,.4)",
    background: selected ? "#ccfbf1" : "#fff",
    color: "#0f172a",
    fontWeight: selected ? 800 : 600,
  } as const;
}

const noticeStyle = {
  margin: 0,
  padding: "12px",
  borderRadius: "12px",
  background: "#f8fafc",
  color: "#475569",
} as const;
